/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * This hook is used to synchronize two query parameters in the URL.
 * It takes two keys and their default values, and ensures that
 * whenever one of the parameters changes, the other one is updated
 * accordingly. This is useful for keeping two related pieces of
 * information in sync in the URL.
 */
export function useSyncedQueryParams<K1 extends string, K2 extends string>(
  key1: K1,
  default1: string,
  key2: K2,
  default2: string,
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const param1 = searchParams.get(key1) ?? default1
  const param2 = searchParams.get(key2) ?? default2

  const [value1, value2] = useMemo(() => [param1, param2], [param1, param2])

  useEffect(() => {
    const cur1 = searchParams.get(key1)
    const cur2 = searchParams.get(key2)
    // bail if already in sync
    // No need for unecessary side effects
    if (cur1 === value1 && cur2 === value2) {
      return
    }
    const next = new URLSearchParams(searchParams)
    next.set(key1, value1)
    next.set(key2, value2)
    setSearchParams(next, { replace: true })
  }, [value1, value2, key1, key2])

  const setValue1 = (v: string) => {
    const n = new URLSearchParams(searchParams)
    n.set(key1, v)
    setSearchParams(n, { replace: true })
  }

  const setValue2 = (v: string) => {
    const n = new URLSearchParams(searchParams)
    n.set(key2, v)
    setSearchParams(n, { replace: true })
  }

  return {
    value1,
    setValue1,
    value2,
    setValue2,
  }
}
